import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';

interface DeliveryDateBlockProps {
  onUpdateFields?: (fields: Record<string, unknown>) => void;
}

const WEEKDAYS_EN = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const WEEKDAYS_ZH = ['日', '一', '二', '三', '四', '五', '六'];

function formatDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

const DeliveryDateBlock: React.FC<DeliveryDateBlockProps> = ({ onUpdateFields }) => {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const weekdays = isZh ? WEEKDAYS_ZH : WEEKDAYS_EN;

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selected, setSelected] = useState<string | null>(null);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const todayStr = formatDate(today);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const handleSelect = (day: number) => {
    const date = new Date(viewYear, viewMonth, day);
    if (date < today) return;
    const dateStr = formatDate(date);
    setSelected(dateStr);
    onUpdateFields?.({ expected_delivery: dateStr });
  };

  // Collapsed after selection
  if (selected) {
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex items-center gap-2 text-xs">
        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
          {isZh ? '交期已設定' : 'Delivery date set'}: <span className="font-mono">{selected}</span>
        </span>
      </div>
    );
  }

  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString(isZh ? 'zh-TW' : 'en-US', { year: 'numeric', month: 'long' });

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-[280px]">
      {/* Header */}
      <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-2 text-xs font-medium text-blue-700 dark:text-blue-400">
        <Calendar className="w-3.5 h-3.5" />
        {isZh ? '選擇交期' : 'Select delivery date'}
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between px-3 py-2">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{monthLabel}</span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 px-2">
        {weekdays.map(d => (
          <div key={d} className="text-center text-[10px] font-medium text-gray-400 dark:text-gray-500 py-1">{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 px-2 pb-2">
        {/* Empty cells before first day */}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = formatDate(new Date(viewYear, viewMonth, day));
          const isPast = new Date(viewYear, viewMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const isToday = dateStr === todayStr;
          const isSunday = new Date(viewYear, viewMonth, day).getDay() === 0;
          const isSaturday = new Date(viewYear, viewMonth, day).getDay() === 6;

          return (
            <button
              key={day}
              disabled={isPast}
              onClick={() => handleSelect(day)}
              className={`text-center py-1.5 text-xs rounded-md transition-colors ${
                isPast
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : isToday
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold hover:bg-blue-200'
                  : isSunday || isSaturday
                  ? 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DeliveryDateBlock;
